import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { observer } from "mobx-react";
import {
  CardElement,
  Elements,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { STRIPE_KEY } from "Static/constant";
import userDetail from "Stores/userDetail";
import { getFeatureFlagWithTeam } from "Utils/commonAPI";
import Utils from "Utils/utils";
import PricingStore from "Stores/Pricing";
import InputTextBox from "Components/common/InputTextBox";
import { toasterState } from "Components/common/toaster";
import {
  invalidCodeConfig,
  transactionDeclinedConfig,
  insufficientFundsConfig,
  PAYMENT_MESSAGES,
} from "Static/stripeCardConstants";
import { SequenceUtils } from "Utils/SequenceUtils";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { MXP_EVENT } from "Static/MixPanelEvents";

const stripePromise = loadStripe(STRIPE_KEY);

const paymentFormObj = {
  address1: "",
  city: "",
  state: "",
  country: "",
  phone: "",
};

const StripeBillingForm = withRouter(
  observer(props => {
    const stripe = useStripe();
    const elements = useElements();

    let formFieldValue = paymentFormObj;

    const { annualPrice, annual, seats, price } = PricingStore.paymentFormInfo;

    const [calculatedPrice, setCalculatedPrice] = useState(0);
    const [cardErr, setCardErr] = useState("");
    const [inputVal, setInputVal] = useState(formFieldValue);
    const [errMsgAddress, setErrMsgAddress] = useState("");
    const [errMsgCity, setErrMsgCity] = useState("");
    const [errMsgState, setErrMsgState] = useState("");
    const [errMsgCountry, setErrMsgCountry] = useState("");
    const [errMsgPhone, setErrMsgPhone] = useState("");
    const [btnLoader, setBtnLoader] = useState(false);
    const [errUserField, setErrUserField] = useState([]);

    const changeInputVal = (id, data) => {
      const stateObj = { ...inputVal };
      stateObj[id] = data;
      updateErrMsgField(id);
      setInputVal(stateObj);
    };

    const updateErrMsgField = id => {
      if (errUserField.length > 0 && errUserField.indexOf(id) !== -1) {
        errUserField.filter(item => item !== id);
        switch (id) {
          case "address1":
            setErrMsgAddress("");
            break;
          case "city":
            setErrMsgCity("");
            break;
          case "state":
            setErrMsgState("");
            break;
          case "country":
            setErrMsgCountry("");
            break;
          case "phone":
            setErrMsgPhone("");
            break;
          default:
            break;
        }
      }
    };

    const validateFormField = async () => {
      let errField = [];
      const { address1, city, state, country, phone } = inputVal;
      if (!Utils.validateStringLength(address1, 1)) {
        setErrMsgAddress("Address is required");
        errField.push("address1");
      }
      if (!Utils.validateStringLength(city, 1)) {
        setErrMsgCity("City is required");
        errField.push("city");
      }
      if (!Utils.validateStringLength(state, 1)) {
        setErrMsgState("State is required");
        errField.push("state");
      }
      if (!Utils.validateStringLength(country, 1)) {
        setErrMsgCountry("Country is required");
        errField.push("country");
      }
      if (!Utils.validateStringLength(phone, 1)) {
        setErrMsgPhone("Phone number is required");
        errField.push("phone");
      } else if (!Utils.isValidPhone(phone)) {
        setErrMsgPhone("Please enter a valid phone number");
        errField.push("phone");
      }
      setErrUserField(errField);
      return errField.length;
    };

    const getStripeCardErrorMsg = errorCode => {
      let errorMsg = "Something went wrong";
      if (errorCode) {
        if (invalidCodeConfig?.errorCodes?.includes(errorCode)) {
          errorMsg = invalidCodeConfig?.errorMsg;
        } else if (transactionDeclinedConfig?.errorCodes?.includes(errorCode)) {
          errorMsg = transactionDeclinedConfig?.errorMsg;
        } else if (insufficientFundsConfig?.errorCodes?.includes(errorCode)) {
          errorMsg = insufficientFundsConfig?.errorMsg;
        }
      }
      return errorMsg;
    };

    const onPaymentSuccess = async () => {
      userDetail.setUserFeatureFlag(await getFeatureFlagWithTeam());
      setBtnLoader(false);
      SequenceUtils.removeActiveSequenceList();
      props.history.push({
        pathname: "/pricing/success",
      });
    };

    const onPaymentPending = () => {
      setBtnLoader(false);
      toasterState.setToastMsg(PAYMENT_MESSAGES.pending, "info");
      props.history.push("/pricing");
    };

    const onPaymentFailed = errorCode => {
      setBtnLoader(false);
      const errorMsg = getStripeCardErrorMsg(errorCode);
      setCardErr(errorMsg);
      confirmationPopupState.setPopupValues({
        dynamicDom: true,
        // No inline style object here: MobX deep-observes this content (stored on
        // confirmationPopupState), and React freezes style prop objects in dev mode
        // on mount, which throws "Dynamic observable objects cannot be frozen".
        content: (
          <>
            <p className="paymentFailedMsg">{PAYMENT_MESSAGES.failed}</p>
          </>
        ),
        actionBtnText: "Chat with Support",
        callback: () => {
          window.Intercom("show");
          confirmationPopupState.setShowPopup(false);
        },
        loadingMsg: "Please wait...",
      });
      confirmationPopupState.setShowPopup(true);
      Utils.mixpanelTrack(MXP_EVENT.PAYMENT_FAILURE_POPUP, {
        error: errorMsg,
      });
      setTimeout(() => {
        setCardErr("");
      }, 5000);
    };

    // Card needs 3D Secure / OTP: open Stripe's verification popup, then let
    // the backend activate the PENDING subscription.
    const handleCardAuthentication = async respData => {
      const { clientSecret, subscriptionId } = respData;
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret
      );
      if (error) {
        onPaymentFailed(error.decline_code || error.code);
        return;
      }
      if (paymentIntent?.status === "succeeded") {
        const confirmResp = await PricingStore.confirmPaymentSuccess({
          subscriptionId,
        });
        if (confirmResp?.success) {
          onPaymentSuccess();
        } else {
          // Stripe has the money; the webhook will activate the plan.
          onPaymentPending();
        }
      } else if (paymentIntent?.status === "processing") {
        onPaymentPending();
      } else {
        onPaymentFailed();
      }
    };

    const paymentSubmit = async event => {
      Utils.preventDefaultFn(event);
      setBtnLoader(true);
      let errCount = await validateFormField();
      if (errCount < 1) {
        const card = elements.getElement(CardElement);
        // createPaymentMethod (not createToken): Stripe's SCA/3D-Secure flow only
        // triggers when the card is attached to the customer as a PaymentMethod.
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: "card",
          card,
          billing_details: {
            phone: inputVal.phone,
            address: {
              line1: inputVal.address1,
              city: inputVal.city,
              state: inputVal.state,
              country: inputVal.country,
            },
          },
        });
        if (error && Object.keys(error).length > 0) {
          setCardErr(error.message);
          setBtnLoader(false);
          setTimeout(() => {
            setCardErr("");
          }, 2000);
          return false;
        }
        if (paymentMethod?.id) {
          const handleCbk = async (value, respData) => {
            if (respData?.requireAction && respData?.clientSecret) {
              handleCardAuthentication(respData);
            } else if (value) {
              onPaymentSuccess();
            } else {
              onPaymentFailed(respData?.declineCode || respData?.errorMessage);
            }
          };
          const { annual, seats, monthlyPlanId, annualPlanId } =
            PricingStore.paymentFormInfo;
          const payload = {
            // Field name kept as stripeToken for the existing API contract; the value
            // is now a PaymentMethod id (pm_...), not a legacy Token (tok_...).
            stripeToken: paymentMethod.id,
            numberOfSeats: 5,
            annual,
            interval: 1,
          };
          payload.numberOfSeats = seats;
          if (annual) {
            payload.planId = annualPlanId;
            payload.interval = 12;
          } else {
            payload.planId = monthlyPlanId;
            payload.interval = 1;
          }
          PricingStore.payment(payload, handleCbk);
        }
      } else {
        setBtnLoader(false);
      }
    };

    const getPaymentPrice = () => {
      if (seats && price) {
        let totalPrice = seats * price;
        if (annual) {
          totalPrice = seats * annualPrice * 12;
        }
        setCalculatedPrice(Utils.numberWithCommas(totalPrice));
      }
    };

    const getChargeNote = () => {
      return (
        <div className="chargeNote">
          You will be charged $1 to verify your payment method. It will be
          refunded immediately.
        </div>
      );
    };

    useEffect(() => {
      getPaymentPrice();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      PricingStore.paymentFormInfo.seats,
      PricingStore.paymentFormInfo.price,
      PricingStore.paymentFormInfo.annualPrice,
      PricingStore.paymentFormInfo.annual,
    ]);

    return (
      <form
        onSubmit={paymentSubmit}
        autoComplete="off"
        name="paymentForm"
        className="paymentForm"
        noValidate={true}
      >
        <h4>Payment Information</h4>
        <div className="cardInputWrapper">
          <CardElement className="cardInput" />
          <span className="errMsgText">{cardErr}</span>
        </div>
        <div className="formDivider">
          <span className="halfCircle right"></span>
          <span className="dashedLine"></span>
          <span className="halfCircle left"></span>
        </div>
        <div className="popupBody">
          <InputTextBox
            name="address1"
            id="address1"
            text="Address"
            type="text"
            value={inputVal.address1}
            change={changeInputVal}
            err={errMsgAddress}
            readOnly={false}
          />
          <div className="formInputGroup">
            <InputTextBox
              name="city"
              id="city"
              text="City"
              type="text"
              value={inputVal.city}
              change={changeInputVal}
              err={errMsgCity}
              readOnly={false}
            />
            <InputTextBox
              name="state"
              id="state"
              text="State"
              type="text"
              value={inputVal.state}
              change={changeInputVal}
              err={errMsgState}
              readOnly={false}
            />
          </div>
          <InputTextBox
            name="country"
            id="country"
            text="Country"
            type="text"
            value={inputVal.country}
            change={changeInputVal}
            err={errMsgCountry}
            readOnly={false}
          />
          <InputTextBox
            name="phone"
            id="phone"
            text="Phone Number"
            type="text"
            value={inputVal.phone}
            change={changeInputVal}
            err={errMsgPhone}
            readOnly={false}
          />
        </div>
        <div className="submitSection">
          <button
            type="submit"
            disabled={!stripe}
            className={`animatedButton ${btnLoader ? "loading" : ""}`}
          >
            {!btnLoader && (
              <span>
                Pay {calculatedPrice !== null ? `$${calculatedPrice}.00` : ""}
              </span>
            )}
            {btnLoader && <span className="load-text"></span>}
          </button>
        </div>
      </form>
    );
  })
);

const StripeBillingCard = props => {
  return (
    <Elements stripe={stripePromise}>
      <StripeBillingForm {...props} />
    </Elements>
  );
};

export default withRouter(observer(StripeBillingCard));
